import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { saveContactForm } from '../mock';
import { Send, Mail, MapPin } from 'lucide-react';

export const Contact = ({ translations, isDark }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await saveContactForm(formData);
      toast.success(translations.contact.form.success);
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error(translations.contact.form.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className={`min-h-screen py-24 transition-colors duration-300 ${
      isDark ? 'bg-zinc-900' : 'bg-zinc-50'
    }`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div className="space-y-8">
            <div className="space-y-4">
              <div className={`inline-block border-2 px-4 py-2 ${
                isDark ? 'border-cyan-400' : 'border-cyan-500'
              }`}>
                <span className={`text-sm uppercase tracking-widest font-mono ${
                  isDark ? 'text-cyan-400' : 'text-cyan-600'
                }`}>Contact</span>
              </div>
              <h2 className={`text-6xl md:text-7xl font-black leading-none tracking-tighter ${
                isDark ? 'text-white' : 'text-zinc-900'
              }`}>
                {translations.contact.title}
              </h2>
              <div className={`h-1 w-24 ${isDark ? 'bg-cyan-400' : 'bg-cyan-500'}`}></div>
            </div>
            
            <p className={`text-xl ${
              isDark ? 'text-zinc-400' : 'text-zinc-700'
            }`}>
              {translations.contact.subtitle}
            </p>

            <div className="space-y-6 pt-8">
              <Card className={`border-2 p-6 transition-all duration-300 ${
                isDark 
                  ? 'bg-zinc-950 border-zinc-800 hover:border-cyan-400'
                  : 'bg-white border-zinc-200 hover:border-cyan-500'
              }`}>
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 border-2 flex items-center justify-center ${
                    isDark ? 'bg-cyan-400/10 border-cyan-400' : 'bg-cyan-500/10 border-cyan-500'
                  }`}>
                    <Mail className={`w-6 h-6 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
                  </div>
                  <div>
                    <div className={`text-xs uppercase tracking-wider ${
                      isDark ? 'text-zinc-500' : 'text-zinc-500'
                    }`}>Email</div>
                    <div className={`font-mono ${isDark ? 'text-white' : 'text-zinc-900'}`}>contact@ascom.pl</div>
                  </div>
                </div>
              </Card>

              <Card className={`border-2 p-6 transition-all duration-300 ${
                isDark 
                  ? 'bg-zinc-950 border-zinc-800 hover:border-cyan-400'
                  : 'bg-white border-zinc-200 hover:border-cyan-500'
              }`}>
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 border-2 flex items-center justify-center ${
                    isDark ? 'bg-cyan-400/10 border-cyan-400' : 'bg-cyan-500/10 border-cyan-500'
                  }`}>
                    <MapPin className={`w-6 h-6 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
                  </div>
                  <div>
                    <div className={`text-xs uppercase tracking-wider ${
                      isDark ? 'text-zinc-500' : 'text-zinc-500'
                    }`}>Location</div>
                    <div className={`font-mono ${isDark ? 'text-white' : 'text-zinc-900'}`}>{translations.footer.location}</div>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          <Card className={`border-2 p-8 ${
            isDark ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-zinc-200'
          }`}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className={`text-sm uppercase tracking-wider font-mono ${
                  isDark ? 'text-zinc-400' : 'text-zinc-600'
                }`}>
                  {translations.contact.form.name}
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={`border-2 transition-colors duration-200 ${
                    isDark 
                      ? 'bg-zinc-900 border-zinc-800 focus:border-cyan-400 text-white'
                      : 'bg-zinc-50 border-zinc-300 focus:border-cyan-500 text-zinc-900'
                  }`}
                  placeholder="John Doe"
                />
              </div>

              <div className="space-y-2">
                <label className={`text-sm uppercase tracking-wider font-mono ${
                  isDark ? 'text-zinc-400' : 'text-zinc-600'
                }`}>
                  {translations.contact.form.email}
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={`border-2 transition-colors duration-200 ${
                    isDark 
                      ? 'bg-zinc-900 border-zinc-800 focus:border-cyan-400 text-white'
                      : 'bg-zinc-50 border-zinc-300 focus:border-cyan-500 text-zinc-900'
                  }`}
                  placeholder="john@example.com"
                />
              </div>

              <div className="space-y-2">
                <label className={`text-sm uppercase tracking-wider font-mono ${
                  isDark ? 'text-zinc-400' : 'text-zinc-600'
                }`}>
                  {translations.contact.form.message}
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className={`border-2 transition-colors duration-200 resize-none ${
                    isDark 
                      ? 'bg-zinc-900 border-zinc-800 focus:border-cyan-400 text-white'
                      : 'bg-zinc-50 border-zinc-300 focus:border-cyan-500 text-zinc-900'
                  }`}
                  placeholder="Your message..."
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className={`w-full font-bold uppercase tracking-wider transition-all duration-200 group ${
                  isDark 
                    ? 'bg-cyan-400 text-black hover:bg-cyan-300'
                    : 'bg-cyan-500 text-white hover:bg-cyan-600'
                }`}
              >
                {isSubmitting ? 'Sending...' : translations.contact.form.submit}
                <Send className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};